import Backbone from "backbone";
import Marionette from "backbone.marionette";
import AnimatedRegion from "marionette-animated-region";

import { MISMATCH_TYPE } from "lib/constants";
import events from "../../events";
import model from "../../models/positions";
import templ from "../../templates/review";

import SoftReviewLocation from "./mismatch/app-mismatch-location";

const SoftReviewSummary = Marionette.View.extend({
    template: templ.SoftReviewSummary,
    events: {
        "click .btn": "onContinue",
    },
    serializeData() {
        return {
            count: this.options.count,
        };
    },
    onContinue() {
        this.trigger("continue");
    },
});

const SoftReviewCommute = Marionette.View.extend({
    template: templ.SoftReviewCommute,
    serializeData() {
        const { position } = this.options;

        return {
            position: position.toJSON(),
            mismatch: position.getCommuteMismatch(),
            progress: this.options.progress,
        };
    },
    events: {
        "click .btn-confirm": "onConfirm",
        "click .btn-cancel": "onCancel",
    },
    onRender() {
        events.trackStep("SOFT_REVIEW_COMMUTE");
    },
    onCancel(event) {
        event.preventDefault();

        this.trigger("soft:finish");
    },
    onConfirm(event) {
        event.preventDefault();

        this.trigger("soft:finish", {
            type: MISMATCH_TYPE.COMMUTE,
            ack: [this.options.position.id],
        });
    },
});

const SHIFT_PREFERENCE_TEXT = {
    1: "Definitely not",
    2: "Preferably not",
    3: "Okay",
    4: "Preferred",
};

const SoftReviewAvail = Marionette.View.extend({
    template: templ.SoftReviewAvail,
    events: {
        "click .btn-confirm": "onConfirm",
        "click .btn-cancel": "onCancel",
    },
    serializeData() {
        const { shift } = this.options.mismatch;
        const pref = SHIFT_PREFERENCE_TEXT[this.options.mismatch.pref];
        const hasIcon = ["day", "swing", "night"].indexOf(shift) > -1;

        let highlight;
        switch (shift) {
            case "day":
            case "swing":
            case "night":
                highlight = "start";
                break;

            case "overtime":
                highlight = "duration";
                break;

            default:
                highlight = "description";
                break;
        }

        const position = this.options.position.toJSON();
        if (position.schedule_start === "Based on route") {
            position.schedule_start = "Start time based on route";
        } else {
            position.schedule_start += " start";
        }

        if (position.schedule_duration === "Based on route") {
            position.schedule_duration = "Shift length based on route";
        } else {
            position.schedule_duration += " long";
        }

        return {
            position,
            mismatch: {
                hasIcon,
                shift,
                pref,
            },
            highlight,
            progress: this.options.progress,
        };
    },
    onCancel(event) {
        event.preventDefault();

        this.trigger("soft:finish");
    },
    onConfirm(event) {
        event.preventDefault();

        this.trigger("soft:finish", {
            type: MISMATCH_TYPE.AVAILABILITY,
            ack: [this.options.position.id],
        });
    },
    onRender() {
        events.trackStep("SOFT_REVIEW_AVAILABILITY");
    },
});

export default Marionette.View.extend({
    template: false,
    regions: {
        container: {
            selector: "#soft-review",
            regionClass: AnimatedRegion,
            animation: {
                showAnimation: [
                    {
                        properties: "transition.slideRightBigIn",
                    },
                ],
                hideAnimation: [
                    {
                        properties: "transition.slideLeftBigOut",
                    },
                ],
            },
        },
    },
    initialize() {
        this.softStep = 0;
        this.softTotal = 0;
        this.softData = {};
        this.positionSkip = [];

        const availPositions = new model.Positions(
            this.options.positions.filter((p) => p.hasAvailMismatch()),
        );

        const commutePositions = new model.Positions(
            this.options.positions.filter((p) => p.hasCommuteMismatch()),
        );

        const locationPositions = new model.Positions(
            this.options.positions.filter((p) => p.hasLocationMismatch()),
        );

        this.softTotal += commutePositions.length;

        this.softReviewSteps = commutePositions.map((p) => ({
            view: SoftReviewCommute,
            options: {
                position: p,
            },
        }));

        if (locationPositions.length > 0) {
            // We only have one step for all location positions
            this.softTotal += 1;

            this.softReviewSteps.push({
                view: SoftReviewLocation,
                options: {
                    positions: locationPositions,
                },
            });
        }

        if (availPositions.length > 0) {
            availPositions.forEach((p) => {
                p.getAvailMismatches().forEach((m) => {
                    this.softTotal += 1;

                    this.softReviewSteps.push({
                        view: SoftReviewAvail,
                        options: {
                            mismatch: m,
                            position: p,
                        },
                    });
                });
            });
        }

        if (this.softTotal > 1) {
            this.showSoftReviewSummary();
        } else {
            this.nextSoftReview();
        }

        const channel = Backbone.Radio.channel("region");
        channel.on("region:shown", (region) => {
            // animated region's transforms mess up position: fixed elements
            region.$el.css("transform", "none");
        });
    },
    showSoftReviewSummary() {
        const view = new SoftReviewSummary({ count: this.softTotal });
        this.showChildView("container", view);

        this.listenTo(view, "continue", this.nextSoftReview);
    },
    nextSoftReview() {
        if (this.softStep + 1 > this.softReviewSteps.length) {
            this.trigger("finish", { softData: this.softData, positionSkip: this.positionSkip });
            return;
        }

        let positions = [];
        const step = this.softReviewSteps[this.softStep];
        const { position, positions: stepPositions = [] } = step.options;

        if (stepPositions.length > 0) {
            positions = stepPositions.map((p) => p.id);
        } else {
            positions = [position.id];
        }

        const skipped = positions.filter((p) => this.positionSkip.indexOf(p.id) > -1);

        // If they've already said they're no longer interested,
        // don't show them further alerts for the some position
        if (skipped.length === positions.length) {
            this.softStep += 1;
            this.nextSoftReview();
            return;
        }

        /* eslint-disable new-cap */
        const view = new step.view({
            user: this.options.user,
            progress: {
                current: this.softStep + 1,
                total: this.softTotal,
            },
            ...step.options,
        });
        /* eslint-enable */

        this.listenTo(view, "soft:finish", (data) => {
            if (data && data.type) {
                const { type } = data;
                if (!this.softData[type]) {
                    this.softData[type] = data;
                } else {
                    this.softData[type].ack = this.softData[type].ack.concat(data.ack);
                }
            } else {
                this.positionSkip = this.positionSkip.concat(positions);
            }

            this.nextSoftReview();
        });
        this.showChildView("container", view);

        this.softStep += 1;
    },
});
